import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faCheck } from '@fortawesome/free-solid-svg-icons';
import ModalOverlay from '../../../components/ui/ModalOverlay/ModalOverlay';
import { useActivityCodes } from '../../../hooks/useActivityCodes';
import { CONFIG } from '../../../config';
import s from '../CaseFilePage.module.css';

interface RecordTimeModalV2Props {
  caseRef: string;
  username: string;
  timeTaskDesc: string;
  setTimeTaskDesc: (v: string) => void;
  timeManualUnits: string;
  setTimeManualUnits: (v: string) => void;
  activityCodeId: string;
  setActivityCodeId: (v: string) => void;
  billable: boolean;
  setBillable: (v: boolean) => void;
  handleStartTimer: () => void;
  handleSaveEntry: () => void;
  onClose: () => void;
}

export default function RecordTimeModalV2({
  caseRef,
  username,
  timeTaskDesc,
  setTimeTaskDesc,
  timeManualUnits,
  setTimeManualUnits,
  activityCodeId,
  setActivityCodeId,
  billable,
  setBillable,
  handleStartTimer,
  handleSaveEntry,
  onClose,
}: RecordTimeModalV2Props) {
  const { data: activityCodes = [], isLoading: codesLoading } = useActivityCodes();

  // Require at least 1 char even if env var is 0; admins can raise via VITE_TR_NARRATIVE_MIN_CHARS or system_config
  const minChars = Math.max(CONFIG.TR_NARRATIVE_MIN_CHARS, 1);
  const narrativeLen = timeTaskDesc.trim().length;
  const narrativeTooShort = narrativeLen < minChars;

  const isManualEntry = timeManualUnits.trim() !== '';
  const formValid = !narrativeTooShort && activityCodeId !== '';

  return (
    <ModalOverlay onClose={onClose} title="Record Time">
      <div className={s.modalForm}>
        <div className={s.formGroup}>
          <label>Case Reference</label>
          <input type="text" value={caseRef} readOnly className={s.readonlyInput} />
        </div>
        <div className={s.formGroup}>
          <label>User</label>
          <input type="text" value={username} readOnly className={s.readonlyInput} />
        </div>
        <div className={s.formGroup}>
          <label>
            Task Description
            {timeTaskDesc.length > 0 && narrativeTooShort && (
              <span style={{ color: '#d32f2f', fontSize: '0.8em', marginLeft: 6 }}>
                ({narrativeLen}/{minChars} chars)
              </span>
            )}
          </label>
          <textarea
            rows={4}
            value={timeTaskDesc}
            onChange={(e) => setTimeTaskDesc(e.target.value)}
            placeholder="Describe the task..."
          />
        </div>
        <div className={s.formGroup}>
          <label>Activity Code</label>
          <select
            value={activityCodeId}
            onChange={(e) => setActivityCodeId(e.target.value)}
            disabled={codesLoading}
          >
            <option value="">— Select —</option>
            {activityCodes.map((ac) => (
              <option key={ac.id} value={String(ac.id)}>
                {ac.code} — {ac.name}
              </option>
            ))}
          </select>
        </div>
        <div className={s.formGroup}>
          <label>Manual Units (optional, 1 unit = 6 min)</label>
          <input
            type="number"
            min={0}
            value={timeManualUnits}
            onChange={(e) => setTimeManualUnits(e.target.value)}
            placeholder="Auto-calculated if left empty"
          />
        </div>
        <div className={s.formGroup}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
            />
            Billable
          </label>
        </div>
        <div className={s.modalActions}>
          <button className={s.btnSecondary} onClick={onClose}>
            Cancel
          </button>
          <button
            className={s.btnPrimary}
            onClick={isManualEntry ? handleSaveEntry : handleStartTimer}
            disabled={!formValid}
            title={
              !formValid
                ? 'Task description (min 15 chars) and activity code are required'
                : undefined
            }
          >
            <FontAwesomeIcon icon={isManualEntry ? faCheck : faPlay} />
            {isManualEntry ? ' Record Entry' : ' Start Timer'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
